import { useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/empty-state";
import { FindingKicker } from "@/components/experience/finding-kicker";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, isValidUuid } from "@/lib/utils";
import {
  WorkshopTopbarContext,
  WorkshopTopbar,
  type WorkshopTopbarContextValue,
} from "@/components/layout/workshop-topbar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertCircle,
  FileQuestion,
  Lock,
  Download,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Server,
  RotateCcw,
} from "lucide-react";
import { LiveWalkthroughViewer } from "./live-walkthrough-viewer";
import { CanEdit } from "@/lib/project-access";
import {
  CitationText,
  CitationPanel,
  CitationModeToggle,
  type ResolvedCitationData,
} from "@/components/citations";
import { toast } from "sonner";
import { MilestoneTimeline } from "@/components/walkthrough/milestone-timeline";
import { buildMilestones, type Milestone } from "@/lib/milestones";
import { features } from "@/lib/features";

type Severity = "critical" | "major" | "minor" | "nit";
type FindingType = "helps" | "hinders" | "missing" | "irrelevant" | "incorrect";

export function RunViewerPage() {
  const { id: projectId, runId } = useParams<{
    id: string;
    runId: string;
  }>();
  const isValidId = isValidUuid(runId);

  const {
    data: walkthrough,
    isLoading,
    error,
    refetch,
  } = trpc.walkthrough.getById.useQuery(
    { id: runId ?? "" },
    {
      enabled: isValidId,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === "running" || status === "pending" ? 3000 : false;
      },
    },
  );

  // Fetch persona + scenario names for topbar
  const { data: persona } = trpc.persona.getById.useQuery(
    { id: walkthrough?.personaId ?? "" },
    { enabled: !!walkthrough?.personaId },
  );
  const { data: scenarioRaw } = trpc.scenario.getById.useQuery(
    { id: walkthrough?.scenarioId ?? "" },
    { enabled: !!walkthrough?.scenarioId },
  );
  // Post-split: the scenario row is the outline+persona binding; the
  // human-readable "name" lives on the embedded outline.
  const scenarioName = scenarioRaw?.outline.name ?? null;
  const scenario = scenarioName ? { name: scenarioName } : null;

  // Runner info for pending/running walkthroughs. A local run followed
  // live runs in the user's agent session, so it has no runner to show.
  const isPendingOrRunning =
    walkthrough?.status === "pending" || walkthrough?.status === "running";
  const needsRunner = isPendingOrRunning && !walkthrough?.liveState;
  const { data: runnerStatus } = trpc.walkthrough.runnerStatus.useQuery(
    { walkthroughId: runId ?? "" },
    {
      enabled: !!runId && needsRunner,
      refetchInterval: needsRunner ? 5000 : false,
    },
  );

  // Track whether the live viewer was ever rendered for THIS run.
  // Prevents redirect-on-completion when user is watching a live stream.
  const wasLiveRef = useRef(false);
  const prevRunIdRef = useRef(runId);
  if (prevRunIdRef.current !== runId) {
    prevRunIdRef.current = runId;
    wasLiveRef.current = false;
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Skeleton className="h-[400px] w-[600px] rounded-lg" />
      </div>
    );
  }

  // Pending walkthrough waiting for a runner — but if a runner is already
  // assigned (it picked up the job and is executing), fall through to the
  // LiveWalkthroughViewer so the SSE stream can resume.
  if (
    walkthrough &&
    walkthrough.status === "pending" &&
    walkthrough.mode === "live_url" &&
    !runnerStatus?.assignedRunner
  ) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-5 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Server className="h-6 w-6 text-muted-foreground animate-pulse" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Waiting for runner</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Looking for an available runner to execute this walkthrough.
              </p>
            </div>
          </div>

          {runnerStatus && (
            <div className="rounded-lg border bg-muted/40 px-4 py-3 text-left">
              {runnerStatus.assignedRunner ? (
                <div className="flex items-center gap-2 text-sm">
                  <span
                    className="inline-block h-2 w-2 rounded-full bg-mark-helps"
                    aria-hidden="true"
                  />
                  Assigned to{" "}
                  <span className="font-medium">
                    {runnerStatus.assignedRunner.name}
                  </span>
                </div>
              ) : runnerStatus.runners.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No runners connected. A runner must register before live
                  walkthroughs can execute.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Available runners
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {runnerStatus.runners.map((r) => (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-1 text-xs"
                      >
                        <span
                          className={cn(
                            "inline-block h-1.5 w-1.5 rounded-full",
                            r.status === "idle"
                              ? "bg-mark-helps"
                              : r.status === "busy"
                                ? "bg-mark-important"
                                : "bg-muted-foreground",
                          )}
                          aria-hidden="true"
                        />
                        {r.name}
                        {r.activeJobCount > 0 && (
                          <span className="text-muted-foreground">
                            ({r.activeJobCount} jobs)
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                  {runnerStatus.queueDepth > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {runnerStatus.queueDepth} job
                      {runnerStatus.queueDepth !== 1 ? "s" : ""} in queue
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pt-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/projects/${projectId ?? ""}/walkthroughs`}>
                Back to Walkthroughs
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidId || error?.data?.code === "NOT_FOUND" || !walkthrough) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24 text-center">
        <EmptyState
          icon={FileQuestion}
          title="Walkthrough run not found"
          description="This run doesn't exist, was deleted, or the link is invalid."
          actions={
            <Button asChild>
              <Link to={`/projects/${projectId ?? ""}/walkthroughs`}>
                Back to Walkthroughs
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24 text-center">
        <EmptyState
          icon={AlertCircle}
          title="Failed to load walkthrough"
          description="Something went wrong while loading this run. Please try again."
          actions={
            <div className="flex items-center gap-2">
              <Button
                onClick={() => refetch()}
                variant="outline"
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Try again
              </Button>
              <Button asChild>
                <Link to={`/projects/${projectId ?? ""}/walkthroughs`}>
                  Back to Walkthroughs
                </Link>
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  // Show a meaningful error state when the walkthrough execution failed
  if (walkthrough.status === "failed") {
    return (
      <WalkthroughFailedView
        walkthrough={walkthrough}
        personaName={persona?.name}
        scenarioName={scenario?.name}
        projectId={projectId!}
        runnerStatus={runnerStatus}
      />
    );
  }

  // Completed walkthroughs go to the report page —
  // unless the user was watching a live stream that just finished.
  if (walkthrough.status === "completed" && !wasLiveRef.current) {
    return (
      <Navigate
        to={`/projects/${projectId}/walkthroughs/${walkthrough.id}`}
        replace
      />
    );
  }

  // Dispatch to the right viewer based on walkthrough mode
  if (walkthrough.mode === "live_url") {
    wasLiveRef.current = true;
    return (
      <LiveWalkthroughViewer
        walkthrough={
          walkthrough as unknown as Parameters<
            typeof LiveWalkthroughViewer
          >[0]["walkthrough"]
        }
        personaName={persona?.name ?? ""}
        scenarioName={scenario?.name ?? ""}
        projectId={projectId!}
      />
    );
  }

  wasLiveRef.current = true;
  return (
    <RunViewerContent
      walkthrough={
        walkthrough as unknown as Parameters<
          typeof RunViewerContent
        >[0]["walkthrough"]
      }
      personaName={persona?.name ?? ""}
      scenarioName={scenario?.name ?? ""}
      projectId={projectId!}
    />
  );
}

/* ─── Failed walkthrough view ─── */
function WalkthroughFailedView({
  walkthrough,
  personaName,
  scenarioName,
  projectId,
  runnerStatus,
}: {
  walkthrough: {
    id: string;
    scenarioId: string;
    personaId: string;
    mode: string;
    errorMessage: string | null;
    targetUrl: string | null;
    createdAt: Date | string;
    clientOrigin?: string | null;
    liveState?: unknown | null;
  };
  personaName?: string;
  scenarioName?: string;
  projectId: string;
  runnerStatus?: {
    runners: Array<{
      id: string;
      name: string;
      status: string;
      activeJobCount: number;
    }>;
    queueDepth: number;
    assignedRunner: { runnerId: string; name: string; status: string } | null;
  };
}) {
  const navigate = useNavigate();
  const isLocal =
    walkthrough.clientOrigin === "local" || !!walkthrough.liveState;
  const retryCompletion = trpc.walkthrough.retryCompletion.useMutation({
    onSuccess: (result) => {
      // Navigate to the run viewer to watch progress
      navigate(`/projects/${projectId}/runs/${walkthrough.id}`, {
        replace: true,
      });
    },
  });

  // Simplify technical error messages for user display
  const userMessage = walkthrough.errorMessage
    ? simplifyErrorMessage(walkthrough.errorMessage)
    : null;

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md space-y-5">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <AlertTriangle className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              This walkthrough didn't complete
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {isLocal
                ? "The agent session ended before the walkthrough finished."
                : "An error occurred before results could be generated. You can try running it again."}
            </p>
          </div>
        </div>

        {/* Error detail */}
        {userMessage && (
          <div className="rounded-lg border bg-muted/40 px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground">
              What happened
            </p>
            <p className="mt-1 text-sm">{userMessage}</p>
          </div>
        )}

        {/* Context */}
        {(scenarioName || personaName) && (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {scenarioName && (
              <span>
                Scenario:{" "}
                <span className="text-foreground">{scenarioName}</span>
              </span>
            )}
            {personaName && (
              <span>
                Persona: <span className="text-foreground">{personaName}</span>
              </span>
            )}
            {walkthrough.targetUrl && (
              <span className="truncate max-w-[220px]">
                URL:{" "}
                <span className="text-foreground">{walkthrough.targetUrl}</span>
              </span>
            )}
          </div>
        )}

        {/* Local run guidance */}
        {isLocal && (
          <div className="rounded-lg border bg-muted/40 px-4 py-3 text-left">
            <p className="text-xs font-medium text-muted-foreground">
              Local walkthrough
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              This run was driven locally from an agent session. To re-run,
              execute{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                iamu run
              </code>{" "}
              in your terminal or start it in your agent session.
            </p>
          </div>
        )}

        {/* Runner info (cloud runs only) */}
        {!isLocal && runnerStatus && (
          <div className="rounded-lg border bg-muted/40 px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground">Runner</p>
            {runnerStatus.runners.length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">
                No runners available. A runner must be connected to execute live
                walkthroughs.
              </p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-2">
                {runnerStatus.runners.map((r) => (
                  <span
                    key={r.id}
                    className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-1 text-xs"
                  >
                    <span
                      className={cn(
                        "inline-block h-1.5 w-1.5 rounded-full",
                        r.status === "idle"
                          ? "bg-mark-helps"
                          : r.status === "busy"
                            ? "bg-mark-important"
                            : "bg-muted-foreground",
                      )}
                      aria-hidden="true"
                    />
                    {r.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-center gap-3 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(
                `/projects/${projectId}/scenarios/${walkthrough.scenarioId}`,
              )
            }
          >
            Back to scenario
          </Button>
          {!isLocal && (
            <CanEdit>
              <Button
                variant="outline"
                size="sm"
                disabled={retryCompletion.isPending}
                onClick={() =>
                  retryCompletion.mutate({
                    walkthroughId: walkthrough.id,
                  })
                }
              >
                {retryCompletion.isPending ? "Retrying..." : "Retry"}
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  navigate(
                    `/projects/${projectId}/scenarios/${walkthrough.scenarioId}?run=true`,
                  )
                }
              >
                Run new
              </Button>
            </CanEdit>
          )}
        </div>
      </div>
    </div>
  );
}

/** Strip stack traces and technical details, keep the actionable part. */
function simplifyErrorMessage(message: string): string {
  // Remove stack traces (lines starting with "at " or containing file paths)
  const firstLine = message
    .split("\n")
    .find(
      (line) =>
        line.trim() &&
        !line.trim().startsWith("at ") &&
        !line.includes("node_modules/"),
    );
  return firstLine?.trim() || "An unexpected error occurred. Please try again.";
}

/* ─── Prototype run viewer ─── */
function RunViewerContent({
  walkthrough,
  personaName,
  scenarioName,
  projectId,
}: {
  walkthrough: {
    id: string;
    personaId: string;
    scenarioId: string;
    prototypeVersionId: string | null;
    steps: Array<{
      id: string;
      stepNumber: number;
      title: string;
      personaReasoning: string | null;
      grounding: string | null;
      pageUrl?: string | null;
      surface?: string | null;
      routeKey?: string | null;
    }>;
    findings: Array<{
      id: string;
      walkthroughStepId: string | null;
      type: string;
      severity: string | null;
      description: string;
      recommendation: string | null;
      prototypeSection: string | null;
      canonicalFindingId: string | null;
      citations: Array<{
        sourceType: string;
        sourceId: string;
        characterization: string | null;
      }>;
    }>;
  };
  personaName: string;
  scenarioName: string;
  projectId: string;
}) {
  const navigate = useNavigate();
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<"prototype" | "steps">("steps");
  const [highlightSection, setHighlightSection] = useState<string | null>(null);
  const [dividerX, setDividerX] = useState<number | null>(null);
  const [panelCitation, setPanelCitation] = useState<{
    sourceType: string;
    sourceId: string;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(
    null,
  );

  const milestones = useMemo(
    () => buildMilestones(walkthrough.steps),
    [walkthrough.steps],
  );

  const activeStep = useMemo(
    () => walkthrough.steps.find((s) => s.id === activeStepId),
    [walkthrough.steps, activeStepId],
  );

  const visibleSteps = useMemo(() => {
    if (!selectedMilestoneId) return walkthrough.steps;
    const m = milestones.find((x) => x.id === selectedMilestoneId);
    if (!m) return walkthrough.steps;
    return walkthrough.steps.filter((s) =>
      m.stepNumbers.includes(s.stepNumber),
    );
  }, [walkthrough.steps, milestones, selectedMilestoneId]);

  const handleSelectMilestone = useCallback((m: Milestone) => {
    setSelectedMilestoneId((prev) => (prev === m.id ? null : m.id));
    const firstStepId = m.stepIds[0];
    if (firstStepId) {
      setActiveStepId(firstStepId);
    }
  }, []);

  const protoVersion: { html?: string } | null = null;

  // Build citations map from findings + grounding footnotes
  const citationsMap = useMemo(() => {
    const map = new Map<string, ResolvedCitationData>();

    // Index by UUID from structured finding citations
    const byUuid: ResolvedCitationData[] = [];
    for (const finding of walkthrough.findings) {
      for (const cit of finding.citations) {
        const name = (cit as { resolvedName?: string | null }).resolvedName;
        const sourceId = (cit as { sourceId?: string }).sourceId;
        if (name && sourceId && !map.has(sourceId)) {
          const data: ResolvedCitationData = {
            sourceType: cit.sourceType,
            sourceId,
            sourceName: name,
            authorityLevel:
              (cit as { resolvedAuthorityLevel?: string | null })
                .resolvedAuthorityLevel ?? "informational",
            contentPreview:
              (cit as { resolvedContent?: string | null }).resolvedContent ??
              "",
            characterization: cit.characterization,
            sourceCategory: (cit as { resolvedSourceCategory?: string | null })
              .resolvedSourceCategory,
          };
          map.set(sourceId, data);
          byUuid.push(data);
        }
      }
    }

    // Parse short labels from grounding footnotes
    const footnoteRe =
      /\[(rule|fact|chunk)-(\d{1,4})\]:\s*(.+?)(?=\[(?:rule|fact|chunk)-\d|\n\n|$)/gis;
    for (const step of walkthrough.steps) {
      if (!step.grounding) continue;
      footnoteRe.lastIndex = 0;
      let footnoteMatch: RegExpExecArray | null;
      while ((footnoteMatch = footnoteRe.exec(step.grounding)) !== null) {
        const [, type, num, footnoteText] = footnoteMatch;
        const label = `${type!.toLowerCase()}-${num}`;
        if (map.has(label)) continue;

        const normalizedFootnote = footnoteText!
          .trim()
          .toLowerCase()
          .slice(0, 80);
        const matched = byUuid.find(
          (c) =>
            c.sourceType === type!.toLowerCase() &&
            c.contentPreview
              .toLowerCase()
              .includes(normalizedFootnote.slice(0, 40)),
        );

        if (matched) {
          map.set(label, matched);
        } else {
          map.set(label, {
            sourceType: type!.toLowerCase(),
            sourceId: label,
            sourceName: "Unknown source",
            authorityLevel: "informational",
            contentPreview: footnoteText!.trim().slice(0, 200),
          });
        }
      }
    }

    return map;
  }, [walkthrough]);

  const openPanel = useCallback((sourceType: string, sourceId: string) => {
    setPanelCitation({ sourceType, sourceId });
  }, []);

  const topbarTitle = [personaName, scenarioName]
    .filter(Boolean)
    .join(" \u00D7 ");

  const backTo = walkthrough.scenarioId
    ? `/projects/${projectId}/scenarios/${walkthrough.scenarioId}`
    : `/projects/${projectId}`;

  const topbarCtx: WorkshopTopbarContextValue = {
    title: topbarTitle || "Walkthrough Run",
    backTo,
    backLabel: walkthrough.scenarioId ? scenarioName || "Scenario" : "Project",
  };

  // Group findings by step
  const findingsByStep = new Map<string | null, typeof walkthrough.findings>();
  for (const f of walkthrough.findings) {
    const key = f.walkthroughStepId;
    const arr = findingsByStep.get(key) ?? [];
    arr.push(f);
    findingsByStep.set(key, arr);
  }

  const orphanFindings = findingsByStep.get(null) ?? [];

  // Severity counts
  const severityCounts = { critical: 0, major: 0, minor: 0, nit: 0 };
  for (const f of walkthrough.findings) {
    const s = f.severity as Severity;
    if (s in severityCounts) severityCounts[s]++;
  }

  const handleStepClick = useCallback(
    (stepId: string, section?: string | null) => {
      setActiveStepId(stepId);
      if (section) setHighlightSection(section);
    },
    [],
  );

  const handleFindingClick = useCallback((section?: string | null) => {
    if (section) setHighlightSection(section);
  }, []);

  const handleExport = useCallback(() => {
    const exportData = {
      id: walkthrough.id,
      persona: personaName,
      scenario: scenarioName,
      exportedAt: new Date().toISOString(),
      summary: {
        totalSteps: walkthrough.steps.length,
        totalFindings: walkthrough.findings.length,
        severityCounts,
      },
      steps: walkthrough.steps,
      findings: walkthrough.findings,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `walkthrough-${walkthrough.id.slice(0, 8)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Walkthrough run exported to JSON");
  }, [walkthrough, personaName, scenarioName, severityCounts]);

  // Resizable divider
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const container = containerRef.current;
      if (!container) return;

      const startWidth =
        dividerX ?? container.getBoundingClientRect().width - 440;

      const onMove = (me: MouseEvent) => {
        const dx = me.clientX - startX;
        const newX = Math.max(
          200,
          Math.min(
            startWidth + dx,
            container.getBoundingClientRect().width - 320,
          ),
        );
        setDividerX(newX);
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [dividerX],
  );

  return (
    <WorkshopTopbarContext.Provider value={topbarCtx}>
      <WorkshopTopbar />
      <div ref={containerRef} className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile view segment toggle */}
        <div className="flex border-b border-border/60 bg-muted/30 p-1 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileTab("prototype")}
            className={cn(
              "flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors",
              mobileTab === "prototype"
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Prototype
          </button>
          <button
            type="button"
            onClick={() => setMobileTab("steps")}
            className={cn(
              "flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors",
              mobileTab === "steps"
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Steps &amp; Findings ({walkthrough.steps.length})
          </button>
        </div>

        {/* Split pane */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: frozen prototype */}
          <div
            className={cn(
              "relative overflow-hidden bg-muted/30 flex-1 min-w-0 w-full",
              mobileTab !== "prototype" && "hidden lg:block",
            )}
            style={dividerX != null ? { width: dividerX } : undefined}
          >
            {/* Frozen label */}
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 rounded-md border border-border/50 bg-background/85 px-2.5 py-1 text-xs text-muted-foreground backdrop-blur-sm">
              <Lock className="h-3 w-3" />
              Frozen
            </div>

            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No prototype linked to this run
            </div>
          </div>

          {/* Divider */}
          <div
            className="group hidden lg:flex w-1.5 shrink-0 cursor-col-resize items-center justify-center bg-border/50 transition-colors hover:bg-primary/30"
            onMouseDown={handleDividerMouseDown}
          >
            <div className="h-8 w-0.5 rounded-full bg-muted-foreground/30 transition-colors group-hover:bg-primary" />
          </div>

          {/* Right: steps + findings */}
          <div
            className={cn(
              "overflow-y-auto w-full lg:w-[440px] lg:min-w-[320px]",
              mobileTab !== "steps" && "hidden lg:block",
            )}
            style={dividerX != null ? { flex: 1, minWidth: 320 } : undefined}
          >
            <div className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium tabular-nums text-muted-foreground">
                  Walkthrough &middot; {walkthrough.steps.length} steps
                </span>
                <div className="flex items-center gap-2">
                  {features.knowledgeSources && <CitationModeToggle />}
                  {walkthrough.steps.length > 0 && (
                    <span className="flex items-center gap-1 text-xs text-finding-helps">
                      <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                      Complete
                    </span>
                  )}
                </div>
              </div>

              {milestones.length > 0 && (
                <div className="mb-4">
                  <MilestoneTimeline
                    milestones={milestones}
                    activeStepNumber={activeStep?.stepNumber}
                    selectedMilestoneId={selectedMilestoneId}
                    onSelectMilestone={handleSelectMilestone}
                    onClearFilter={() => setSelectedMilestoneId(null)}
                  />
                </div>
              )}

              {/* Steps + interleaved findings */}
              {visibleSteps.map((step) => {
                const stepFindings = findingsByStep.get(step.id) ?? [];
                const isActive = activeStepId === step.id;

                return (
                  <div key={step.id} className="mb-2">
                    <div
                      onClick={() => handleStepClick(step.id)}
                      className={cn(
                        "w-full rounded-lg border p-3 text-left transition-colors cursor-pointer",
                        isActive
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-muted-foreground/30",
                      )}
                    >
                      <div className="flex items-start gap-2.5">
                        <span
                          className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {step.stepNumber}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold">{step.title}</p>
                          {step.personaReasoning && (
                            <div className="mt-1 text-sm text-muted-foreground">
                              <CitationText
                                text={step.personaReasoning}
                                citations={citationsMap}
                                onOpenPanel={openPanel}
                              />
                            </div>
                          )}
                          {step.grounding && (
                            <div className="mt-1.5 border-t pt-1.5 text-sm text-muted-foreground">
                              <CitationText
                                text={step.grounding}
                                citations={citationsMap}
                                onOpenPanel={openPanel}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Findings nested under this step */}
                    {stepFindings.map((finding) => (
                      <FindingCard
                        key={finding.id}
                        finding={finding}
                        onClick={() =>
                          handleFindingClick(finding.prototypeSection)
                        }
                      />
                    ))}
                  </div>
                );
              })}

              {/* Orphan findings */}
              {orphanFindings.length > 0 && (
                <div className="mt-4">
                  <span className="mb-2 block text-sm font-medium text-muted-foreground">
                    General findings
                  </span>
                  {orphanFindings.map((finding) => (
                    <FindingCard
                      key={finding.id}
                      finding={finding}
                      onClick={() =>
                        handleFindingClick(finding.prototypeSection)
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary bar */}
        <div className="flex shrink-0 items-center justify-between border-t bg-muted/50 px-4 py-2.5">
          <div className="flex items-center gap-3 text-sm tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground">
              {walkthrough.findings.length} findings
            </span>
            {severityCounts.critical > 0 && (
              <span className="flex items-center gap-1">
                <span
                  className="h-1.5 w-1.5 rounded-full bg-mark-critical"
                  aria-hidden="true"
                />
                {severityCounts.critical} critical
              </span>
            )}
            {severityCounts.major > 0 && (
              <span className="flex items-center gap-1">
                <span
                  className="h-1.5 w-1.5 rounded-full bg-mark-important"
                  aria-hidden="true"
                />
                {severityCounts.major} major
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export JSON
            </Button>
            <Button
              size="sm"
              onClick={() => navigate(`/projects/${projectId}/findings`)}
            >
              Review Findings
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Citation Side Panel */}
      <CitationPanel
        open={panelCitation !== null}
        onOpenChange={(open) => {
          if (!open) setPanelCitation(null);
        }}
        sourceType={panelCitation?.sourceType ?? null}
        sourceId={panelCitation?.sourceId ?? null}
        projectId={projectId}
      />
    </WorkshopTopbarContext.Provider>
  );
}

/* ─── Finding card (shared) ─── */
const severityStyles: Record<Severity, string> = {
  critical: "border-l-mark-critical bg-mark-critical/5",
  major: "border-l-mark-important bg-mark-important/5",
  minor: "border-l-mark-minor bg-mark-minor/5",
  nit: "border-l-border bg-muted/40",
};

export function FindingCard({
  finding,
  onClick,
}: {
  finding: {
    type: string;
    severity: string | null;
    description: string;
    citations: Array<{
      sourceType: string;
      sourceId?: string;
      characterization: string | null;
      resolvedName?: string | null;
      resolvedAuthorityLevel?: string | null;
      resolvedContent?: string | null;
    }>;
  };
  onClick?: () => void;
}) {
  const severity = (finding.severity ?? "nit") as Severity;
  const type = finding.type as FindingType;

  const maxVisible = 4;
  const visibleCitations = finding.citations.slice(0, maxVisible);
  const overflowCount = finding.citations.length - maxVisible;

  return (
    <div
      onClick={onClick}
      className={cn(
        "mt-1 ml-8 w-[calc(100%-2rem)] rounded-md border-l-[3px] p-2.5 text-left transition-colors",
        onClick && "cursor-pointer hover:brightness-110",
        severityStyles[severity] ?? severityStyles.nit,
      )}
    >
      <FindingKicker severity={severity} type={type} className="mb-1.5" />
      <p className="text-sm text-foreground">{finding.description}</p>
      {finding.citations.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {visibleCitations.map((c, i) => {
            const label = c.resolvedName
              ? `${c.sourceType} \u00B7 ${c.resolvedName}`
              : (c.characterization ?? c.sourceType);

            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <span
                    role="button"
                    tabIndex={0}
                    className="inline-flex max-w-[180px] cursor-help items-center gap-1 truncate rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                  >
                    {label}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs space-y-1 text-left"
                >
                  {c.resolvedName && (
                    <p className="font-semibold">{c.resolvedName}</p>
                  )}
                  {c.resolvedAuthorityLevel && (
                    <p className="text-xs">
                      {c.sourceType} &middot; {c.resolvedAuthorityLevel}
                    </p>
                  )}
                  {c.characterization && (
                    <p className="text-xs">{c.characterization}</p>
                  )}
                  {c.resolvedContent && (
                    <p className="text-xs">{c.resolvedContent}</p>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
          {overflowCount > 0 && (
            <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              +{overflowCount} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}
